// aiCommandEngine.js
// ============================================
// Local Rule-Based AI Command Engine
// Kono paid API lage na — pattern/keyword matching diye kaj kore.
// ============================================

export const COLOR_MAP = {
  "red": "#ef4444", "lal": "#ef4444", "লাল": "#ef4444",
  "blue": "#3b82f6", "nil": "#3b82f6", "নীল": "#3b82f6",
  "green": "#22c55e", "sobuj": "#22c55e", "সবুজ": "#22c55e",
  "yellow": "#eab308", "holud": "#eab308", "হলুদ": "#eab308",
  "orange": "#f97316", "kmola": "#f97316", "কমলা": "#f97316",
  "purple": "#a855f7", "beguni": "#a855f7", "বেগুনি": "#a855f7",
  "black": "#111827", "kalo": "#111827", "কালো": "#111827",
  "white": "#f8fafc", "sada": "#f8fafc", "সাদা": "#f8fafc",
  "gray": "#6b7280", "grey": "#6b7280", "dhusor": "#6b7280", "ধূসর": "#6b7280",
};

function findColorInText(text) {
  const lower = text.toLowerCase();
  for (const key in COLOR_MAP) {
    if (lower.includes(key.toLowerCase())) return COLOR_MAP[key];
  }
  return null;
}

function normalizeDigits(value) {
  const digitMap = { "০":"0","১":"1","২":"2","৩":"3","৪":"4","৫":"5","৬":"6","৭":"7","৮":"8","৯":"9" };
  return String(value || "").replace(/[০-৯]/g, (digit) => digitMap[digit] || digit);
}

function escapeRegex(value) {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ============================================
// SAFE COLUMN MATCHING (word-boundary, no false hits
// on single-letter columns like "A", "B")
// ============================================
function findColumnInText(text, columns) {
  if (!columns || !text) return null;
  const normalizedText = " " + text.toLowerCase() + " ";
  const sorted = [...columns].sort((a, b) => String(b).length - String(a).length);
  for (const c of sorted) {
    const name = String(c).toLowerCase().trim();
    if (!name) continue;
    const regex = new RegExp(`(^|[^a-zA-Z0-9\u0980-\u09FF])${escapeRegex(name)}([^a-zA-Z0-9\u0980-\u09FF]|$)`, "i");
    if (regex.test(normalizedText)) return c;
  }
  return null;
}

function findAllColumnsInText(text, columns) {
  if (!columns || !text) return [];
  const normalizedText = " " + text.toLowerCase() + " ";
  const sorted = [...columns].sort((a, b) => String(b).length - String(a).length);
  const found = [];
  sorted.forEach((c) => {
    const name = String(c).toLowerCase().trim();
    if (!name) return;
    const regex = new RegExp(`(^|[^a-zA-Z0-9\u0980-\u09FF])${escapeRegex(name)}([^a-zA-Z0-9\u0980-\u09FF]|$)`, "i");
    if (regex.test(normalizedText)) found.push(c);
  });
  return columns.filter((c) => found.includes(c));
}

function findNumberBefore(text, keywords) {
  const normalizedText = normalizeDigits(text);
  for (const kw of keywords) {
    const safeKeyword = escapeRegex(kw);
    const regex = new RegExp("(\\d+)\\s*(?:ta|টা)?\\s*" + safeKeyword, "i");
    const match = normalizedText.match(regex);
    if (match) return parseInt(match[1], 10);
  }
  return null;
}

// ============================================
// COMMAND: Table Create (row/column/name/color)
// ============================================
export const ROW_WORDS = ["row", "রো", "সারি"];
export const COLUMN_WORDS = ["column", "কলাম"];

function tryParseTableCreate(text) {
  const rowCount = findNumberBefore(text, ROW_WORDS);
  const colCount = findNumberBefore(text, COLUMN_WORDS);
  if (!rowCount && !colCount) return null;

  const color = findColorInText(text);
  let columnNames = [];
  const nameMatch = text.match(/(?:column name|কলাম নাম)[:\s]+([^.]+)/i);
  if (nameMatch) {
    columnNames = nameMatch[1].split(/[,،]/).map((s) => s.trim()).filter(Boolean);
  }

  return {
    type: "create_table",
    rows: rowCount || 5,
    columns: colCount || (columnNames.length || 3),
    columnNames,
    color,
  };
}

// ============================================
// COMMAND: Column Math (jog/biyog/gun/bhag)
// ============================================
export const OPERATOR_WORDS = {
  "+": ["jog", "যোগ", "sum", "add", "plus", "+"],
  "-": ["biyog", "বিয়োগ", "subtract", "minus", "-"],
  "*": ["gun", "গুণ", "multiply", "times", "*"],
  "/": ["bhag", "ভাগ", "divide", "/"],
};

function findOperator(text) {
  const lower = text.toLowerCase();
  for (const op in OPERATOR_WORDS) {
    for (const word of OPERATOR_WORDS[op]) {
      if (lower.includes(word.toLowerCase())) return op;
    }
  }
  return null;
}

function tryParseColumnMath(text, availableColumns) {
  if (!availableColumns || availableColumns.length < 2) return null;
  const matchedCols = findAllColumnsInText(text, availableColumns);
  if (matchedCols.length < 2) return null;

  const operator = findOperator(text);
  if (!operator) return null;

  let resultCol = "Result";
  const nameMatch = text.match(/(?:result column|result name|ফলাফল কলাম)[:\s]+([a-zA-Z\u0980-\u09FF ]+)/i);
  if (nameMatch) resultCol = nameMatch[1].trim();

  return { type: "column_math", colA: matchedCols[0], colB: matchedCols[1], operator, resultCol };
}

// ============================================
// COMMAND: Sort
// ============================================
export const ASC_WORDS = ["ascending", "choto theke boro", "chotoyo theke boro", "a-z", "বাড়া"];
export const DESC_WORDS = ["descending", "boro theke choto", "boroyo theke choto", "z-a", "কমা"];
export const SORT_WORDS = ["sort", "soro", "সাজা", "সর্ট"];

function tryParseSort(text, availableColumns) {
  const lower = text.toLowerCase();
  const hasSortWord = SORT_WORDS.some((w) => lower.includes(w));
  if (!hasSortWord) return null;
  const matchedCol = findColumnInText(text, availableColumns);
  if (!matchedCol) return null;

  let direction = "asc";
  if (DESC_WORDS.some((w) => lower.includes(w))) direction = "desc";
  else if (ASC_WORDS.some((w) => lower.includes(w))) direction = "asc";

  return { type: "sort_column", column: matchedCol, direction };
}

// ============================================
// COMMAND: Search / Filter
// ============================================
export const SEARCH_WORDS = ["khujo", "khoj", "খুঁজ", "search", "filter", "dekhao", "দেখা"];

function tryParseSearch(text) {
  const lower = text.toLowerCase();
  const hasSearchWord = SEARCH_WORDS.some((w) => lower.includes(w));
  if (!hasSearchWord) return null;

  let query = text;
  for (const w of SEARCH_WORDS) {
    const idx = lower.indexOf(w);
    if (idx !== -1) { query = text.slice(idx + w.length).trim(); break; }
  }
  query = query.replace(/^[:\-,\s]+/, "").trim();
  if (!query) return null;

  return { type: "search_filter", query };
}

// ============================================
// COMMAND: Column / Row targeted color
// ============================================
export function parseColorCommands(text) {
  if (!text) return [];
  const instructions = [];
  const clauses = text.split(/[,;]|(?:\band\b)|(?:আর)/i).map((c) => c.trim()).filter(Boolean);

  clauses.forEach((clause) => {
    const lower = clause.toLowerCase();
    const color = findColorInText(clause);
    if (!color) return;

    const colMatch = lower.match(/(?:column|কলাম|কলম)\s*(\d+)/i);
    if (colMatch) { instructions.push({ type: "column", index: parseInt(colMatch[1], 10), color }); return; }

    const rowMatch = lower.match(/(?:row|সারি|রো)\s*(\d+)/i);
    if (rowMatch) { instructions.push({ type: "row", index: parseInt(rowMatch[1], 10), color }); return; }
  });

  return instructions;
}

// ============================================
// COMMAND: Auto Serial / Number Fill
// ============================================
export const SERIAL_WORDS = ["serial", "সিরিয়াল", "auto number", "নম্বর দাও", "number diye"];

export function parseAutoSerial(text, columns) {
  const lower = text.toLowerCase();
  const hasWord = SERIAL_WORDS.some((w) => lower.includes(w));
  if (!hasWord) return null;

  const targetCol = findColumnInText(text, columns);
  return { type: "auto_serial", column: targetCol || columns?.[0] || null };
}

// ============================================
// COMMAND: Find & Replace
// ============================================
export const REPLACE_WORDS = ["replace", "change", "poriborton", "পরিবর্তন", "die", "diye"];

export function parseFindReplace(text, columns) {
  const quoted = [...text.matchAll(/['"]([^'"]+)['"]/g)].map((m) => m[1]);
  if (quoted.length < 2) return null;

  const lower = text.toLowerCase();
  const hasReplaceWord = REPLACE_WORDS.some((w) => lower.includes(w));
  if (!hasReplaceWord) return null;

  const targetCol = findColumnInText(text, columns);
  return { type: "find_replace", find: quoted[0], replaceWith: quoted[1], column: targetCol || null };
}

// ============================================
// COMMAND: Merge Columns
// ============================================
export const MERGE_WORDS = ["merge", "combine", "jog kore", "মার্জ", "একত্র"];

export function parseMergeColumns(text, columns) {
  if (!columns) return null;
  const lower = text.toLowerCase();
  const hasMergeWord = MERGE_WORDS.some((w) => lower.includes(w));
  if (!hasMergeWord) return null;

  const matchedCols = findAllColumnsInText(text, columns);
  if (matchedCols.length < 2) return null;

  let newColName = `${matchedCols[0]} ${matchedCols[1]}`;
  const nameMatch = text.match(/([a-zA-Z\u0980-\u09FF]+(?:\s[a-zA-Z\u0980-\u09FF]+)?)\s+(?:banao|তৈরি করো|বানাও)/i);
  if (nameMatch) {
    const candidate = nameMatch[1].trim();
    const isJustAColumnName = matchedCols.some((c) => candidate.toLowerCase() === String(c).toLowerCase());
    if (!isJustAColumnName) newColName = candidate;
  }

  return { type: "merge_columns", colA: matchedCols[0], colB: matchedCols[1], newColName };
}

// ============================================
// COMMAND: Split Column
// ============================================
export const SPLIT_WORDS = ["split", "বিভক্ত", "vag koro", "ভাগ কর"];

export function parseSplitColumn(text, columns) {
  if (!columns) return null;
  const lower = text.toLowerCase();
  const hasSplitWord = SPLIT_WORDS.some((w) => lower.includes(w));
  if (!hasSplitWord) return null;

  const matchedCol = findColumnInText(text, columns);
  if (!matchedCol) return null;

  let delimiter = " ";
  if (lower.includes("comma") || lower.includes("কমা")) delimiter = ",";

  return { type: "split_column", column: matchedCol, delimiter };
}

// ============================================
// COMMAND: Conditional Highlight
// ============================================
export function parseConditionalRule(text, columns) {
  if (!columns) return null;
  const lower = text.toLowerCase();
  const targetCol = findColumnInText(text, columns);
  if (!targetCol) return null;

  const color = findColorInText(text);
  if (!color) return null;

  const numMatch = text.match(/(\d+(?:\.\d+)?)/);
  const GT_WORDS = ["beshi", "বেশি", "greater", "more than", "above", "boro"];
  const LT_WORDS = ["kom", "কম", "less than", "below", "choto"];

  let type = null;
  if (GT_WORDS.some((w) => lower.includes(w))) type = "gt";
  else if (LT_WORDS.some((w) => lower.includes(w))) type = "lt";

  if (type && numMatch) {
    return { col: targetCol, type, value: numMatch[1], bgColor: color };
  }

  const eqRegex = new RegExp(`${escapeRegex(targetCol)}\\s+([a-zA-Z\\u0980-\\u09FF]+)\\s+(?:hole|হলে)`, "i");
  const eqMatch = text.match(eqRegex);
  if (eqMatch) {
    return { col: targetCol, type: "eq", value: eqMatch[1], bgColor: color };
  }

  return null;
}

// ============================================
// COMMAND: Email / Phone Validate
// ============================================
export function parseValidateCommand(text, columns) {
  if (!columns) return null;
  const lower = text.toLowerCase();
  const hasValidateWord = ["validate", "check", "চেক", "ভ্যালিডেট", "shothik"].some((w) => lower.includes(w));
  if (!hasValidateWord) return null;

  let kind = null;
  if (lower.includes("email") || lower.includes("ইমেইল")) kind = "email";
  else if (lower.includes("phone") || lower.includes("ফোন") || lower.includes("mobile")) kind = "phone";
  if (!kind) return null;

  const targetCol = findColumnInText(text, columns) || columns.find((c) => String(c).toLowerCase().includes(kind));
  if (!targetCol) return null;

  return { type: "validate", column: targetCol, kind };
}

// ============================================
// Main entry point
// ============================================
export function runAICommand(promptText, context = {}) {
  const text = (promptText || "").trim();
  if (!text) return { type: "error", message: "Kichu likhun age." };

  if (context.columns) {
    const sortResult = tryParseSort(text, context.columns);
    if (sortResult) return sortResult;

    const mathResult = tryParseColumnMath(text, context.columns);
    if (mathResult) return mathResult;
  }

  const searchResult = tryParseSearch(text);
  if (searchResult) return searchResult;

  const tableResult = tryParseTableCreate(text);
  if (tableResult) return tableResult;

  return { type: "unknown", message: "Bujhte parini, ektu onno vabe bolun." };
}
// ============================================
// COMMAND: Days Between Dates
// (e.g. "Join Date ar Exit Date gap koto din", "Join Date theke aj koto din")
// ============================================
export const DAYS_WORDS = ["koto din", "days between", "din gap", "দিন"];
export const TODAY_WORDS = ["today", "aj", "আজ"];

export function parseDaysBetween(text, columns) {
  const lower = text.toLowerCase();
  const hasDaysWord = DAYS_WORDS.some((w) => lower.includes(w));
  if (!hasDaysWord) return null;

  const matchedCols = findAllColumnsInText(text, columns);
  const useToday = TODAY_WORDS.some((w) => lower.includes(w));

  if (matchedCols.length >= 2) {
    return { type: "days_between", colA: matchedCols[0], colB: matchedCols[1], useToday: false };
  }
  if (matchedCols.length === 1 && useToday) {
    return { type: "days_between", colA: matchedCols[0], colB: null, useToday: true };
  }
  return null;
}

// ============================================
// COMMAND: Percentage of Total
// (e.g. "Bonus percent of total")
// ============================================
export const PERCENT_WORDS = ["percent", "percentage", "শতাংশ"];

export function parsePercentOfTotal(text, columns) {
  const lower = text.toLowerCase();
  const hasPercentWord = PERCENT_WORDS.some((w) => lower.includes(w));
  if (!hasPercentWord) return null;
  const targetCol = findColumnInText(text, columns);
  if (!targetCol) return null;
  return { type: "percent_of_total", column: targetCol };
}

// ============================================
// COMMAND: Group & Count
// (e.g. "Department diye group koro")
// ============================================
export const GROUP_WORDS = ["group", "গ্রুপ"];

export function parseGroupCount(text, columns) {
  const lower = text.toLowerCase();
  const hasGroupWord = GROUP_WORDS.some((w) => lower.includes(w));
  if (!hasGroupWord) return null;
  const targetCol = findColumnInText(text, columns);
  if (!targetCol) return null;
  return { type: "group_count", column: targetCol };
}